/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { SurveyUserRoles, UtrvUserRole } from '@constants/user';
import { InitiativePermissions } from './InitiativePermissions';
import { SurveyPermissions } from './SurveyPermissions';
import { PermissionsSurvey, PermissionsUser, PermissionsUtrv } from './types';
import { WorkgroupPermissions } from './WorkgroupPermissions';

export class UtrvPermissions {
  static canContributeRoles: SurveyUserRoles.Stakeholder[] = [SurveyUserRoles.Stakeholder];
  static canVerifyRoles: SurveyUserRoles.Verifier[] = [SurveyUserRoles.Verifier];
  static canAccessRoles: UtrvUserRole[] = [SurveyUserRoles.Stakeholder, SurveyUserRoles.Verifier];

  public static canViewMetricPage(
    utrv: PermissionsUtrv,
    survey: PermissionsSurvey | undefined,
    user: PermissionsUser,
  ): boolean {
    if (this.canExplicitlyContribute(utrv, user) || this.canExplicitlyVerify(utrv, user)) {
      return true;
    }
    const surveyId = utrv.compositeData?.surveyId;
    if (survey && surveyId && survey._id === surveyId) {
      const hasWorkgroupPermission = WorkgroupPermissions.checkHasUtrvsUserRoles({
        utrvs: [utrv],
        userId: user._id,
        roles: UtrvPermissions.canAccessRoles,
        survey,
      });
      if (hasWorkgroupPermission) {
        return true;
      }

      return SurveyPermissions.canAccessAllData(survey, user);
    }
    const initiativeId = utrv.initiativeId;
    return InitiativePermissions.canAccessAllSurveyData(user, initiativeId);
  }

  public static canContribute(
    utrv: PermissionsUtrv,
    survey: PermissionsSurvey | undefined,
    user: PermissionsUser,
  ): boolean {
    if (this.canExplicitlyContribute(utrv, user)) {
      return true;
    }

    const surveyId = utrv.compositeData?.surveyId;
    if (survey && surveyId && survey._id === surveyId) {
      const hasWorkgroupPermission = WorkgroupPermissions.checkHasUtrvsUserRoles({
        utrvs: [utrv],
        userId: user._id,
        roles: UtrvPermissions.canContributeRoles,
        survey,
      });
      if (hasWorkgroupPermission) {
        return true;
      }

      return SurveyPermissions.canContribute(survey, user);
    }

    const initiativeId = utrv.initiativeId;
    return InitiativePermissions.canContribute(user, initiativeId);
  }

  private static canExplicitlyContribute(utrv: PermissionsUtrv, user: PermissionsUser): boolean {
    return utrv.stakeholders.stakeholder.includes(user._id);
  }

  public static canVerify(
    utrv: PermissionsUtrv,
    survey: PermissionsSurvey | undefined,
    user: PermissionsUser,
  ): boolean {
    if (this.canExplicitlyVerify(utrv, user)) {
      return true;
    }

    const surveyId = utrv.compositeData?.surveyId;
    if (survey && surveyId && survey._id === surveyId) {
      const hasWorkgroupPermission = WorkgroupPermissions.checkHasUtrvsUserRoles({
        utrvs: [utrv],
        userId: user._id,
        roles: UtrvPermissions.canVerifyRoles,
        survey,
      });
      if (hasWorkgroupPermission) {
        return true;
      }

      return SurveyPermissions.canVerify(survey, user);
    }

    const initiativeId = utrv.initiativeId;
    return InitiativePermissions.canVerify(user, initiativeId);
  }

  private static canExplicitlyVerify(utrv: PermissionsUtrv, user: PermissionsUser): boolean {
    return utrv.stakeholders.verifier.includes(user._id);
  }

  public static canExplicitlyBulk({
    utrvs,
    survey,
    user,
    role,
    every = true,
  }: {
    utrvs: PermissionsUtrv[];
    survey: Pick<PermissionsSurvey, '_id'>;
    user: Pick<PermissionsUser, '_id'>;
    role: UtrvUserRole;
    every?: boolean;
  }): boolean {
    const userId = user._id;
    const surveyId = survey._id;
    const mismatchSurvey = [];
    const utrvCheck = utrvs[every ? 'every' : 'some']((utrv) => {
      const userIds = utrv.stakeholders[role];
      const utrvSurveyId = utrv.compositeData?.surveyId;
      if (surveyId && surveyId !== utrvSurveyId) {
        mismatchSurvey.push(utrv);
        return false;
      }
      return userIds.includes(userId);
    });
    if (mismatchSurvey.length > 0) {
      return false;
    }

    return utrvCheck;
  }

  public static canContributeBulk(utrvs: PermissionsUtrv[], survey: PermissionsSurvey, user: PermissionsUser): boolean {
    if (this.canExplicitlyBulk({ utrvs, survey, user, role: SurveyUserRoles.Stakeholder })) {
      return true;
    }

    const userId = user._id;
    const hasWorkgroupPermission = WorkgroupPermissions.checkHasUtrvsUserRoles({
      utrvs,
      userId,
      roles: UtrvPermissions.canContributeRoles,
      survey,
    });
    if (hasWorkgroupPermission) {
      return true;
    }

    return SurveyPermissions.canContribute(survey, user);
  }

  public static canVerifyBulk(utrvs: PermissionsUtrv[], survey: PermissionsSurvey, user: PermissionsUser): boolean {
    if (this.canExplicitlyBulk({ utrvs, survey, user, role: SurveyUserRoles.Verifier })) {
      return true;
    }

    const userId = user._id;
    const hasWorkgroupPermission = WorkgroupPermissions.checkHasUtrvsUserRoles({
      utrvs,
      userId,
      roles: UtrvPermissions.canVerifyRoles,
      survey,
    });
    if (hasWorkgroupPermission) {
      return true;
    }

    return SurveyPermissions.canVerify(survey, user);
  }
}
